<script setup lang="ts">
import { ref } from "vue";
import AnimateHeight from "vue-animate-height";

const props = defineProps<{
  toolName: string;
  input: Record<string, unknown>;
  approvalId: string;
}>();

const emit = defineEmits<{
  approve: [approvalId: string];
  deny: [approvalId: string];
}>();

const expanded = ref(false);

function onApprove() {
  emit("approve", props.approvalId);
}

function onDeny() {
  emit("deny", props.approvalId);
}
</script>

<template>
  <div
    class="chat-tool-block mb-px-8 rounded-8 overflow-hidden border"
    role="group"
    :aria-label="`Approval required for ${toolName}`">
    <button
      type="button"
      class="chat-tool-button gap-px-8 px-px-12 py-px-8 focus-ring flex w-full items-center"
      :aria-expanded="expanded"
      :aria-controls="`approval-details-${approvalId}`"
      @click="expanded = !expanded">
      <font-awesome-icon
        :icon="['fas', 'shield-halved']"
        class="chat-tool-success icon-xs shrink-0" />
      <span class="chat-text-main t-xs">Approval needed: {{ toolName }}</span>
      <font-awesome-icon
        :icon="['fas', 'chevron-down']"
        class="chat-text-muted icon-xs duration-fast ease-standard ml-auto transition-transform"
        :class="{ 'rotate-180': expanded }" />
    </button>

    <AnimateHeight :height="expanded ? 'auto' : 0">
      <div
        :id="`approval-details-${approvalId}`"
        class="px-px-12 pb-px-8">
        <p class="chat-text-muted t-caption-bold mb-px-4">Arguments</p>
        <pre class="chat-code-block chat-text-main rounded-4 px-px-8 py-px-6 t-caption overflow-x-auto whitespace-pre-wrap">{{
          JSON.stringify(input, null, 2)
        }}</pre>
      </div>
    </AnimateHeight>

    <div class="gap-px-8 px-px-12 pb-px-8 flex items-center justify-end">
      <button
        type="button"
        class="chat-button-subtle rounded-4 px-px-10 py-px-4 t-caption focus-ring interactive-active"
        aria-label="Deny tool execution"
        @click="onDeny">
        Deny
      </button>
      <button
        type="button"
        class="rounded-4 px-px-10 py-px-4 t-caption focus-ring interactive-active bg-app-blue-600 dark:bg-app-blue-fill text-white"
        aria-label="Allow tool execution"
        @click="onApprove">
        Allow
      </button>
    </div>
  </div>
</template>
